import React from 'react';
import { Avatar, Card, Dropdown, Space, Tag, Typography, theme } from 'antd';
import type { MenuProps } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  HolderOutlined,
  MailOutlined,
  PhoneOutlined,
  SwapOutlined,
  UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import type { Lead } from '@/hooks/useLeads';
import { useCurrency } from '@/contexts/CurrencyContext';
import {
  LEAD_STATUSES,
  PRIORITY_COLORS,
  STATUS_COLORS,
  companyInitials,
  isFollowUpOverdue,
  leadAnnualRevenue,
} from './leads.constants';

const { Text } = Typography;

export type LeadKanbanCardProps = {
  lead: Lead;
  ownerLabel: string;
  isUpdating?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement> | null;
  onView: () => void;
  onEdit: (e?: React.MouseEvent) => void;
  onDelete: (e?: React.MouseEvent) => void;
  onMoveStatus: (status: string) => void;
};

export const LeadKanbanCard: React.FC<LeadKanbanCardProps> = ({
  lead,
  ownerLabel,
  isUpdating,
  dragHandleProps,
  onView,
  onEdit,
  onDelete,
  onMoveStatus,
}) => {
  const { token } = theme.useToken();
  const { formatCurrency } = useCurrency();
  const st = lead.lead_status;
  const border = STATUS_COLORS[st] || token.colorBorder;
  const rev = leadAnnualRevenue(lead);
  const overdue = isFollowUpOverdue(lead.next_follow_up_date);
  const converted = !!lead.is_converted;

  const moveItems: MenuProps['items'] = LEAD_STATUSES.filter((s) => s !== st).map((s) => ({
    key: s,
    label: `Move to ${s}`,
    onClick: () => onMoveStatus(s),
  }));

  const menuItems: MenuProps['items'] = [
    { key: 'view', icon: <EyeOutlined />, label: 'View', onClick: onView },
    { key: 'edit', icon: <EditOutlined />, label: 'Edit', onClick: () => onEdit() },
    { key: 'move', icon: <SwapOutlined />, label: 'Move to…', children: moveItems },
    { type: 'divider' },
    { key: 'delete', icon: <DeleteOutlined />, label: 'Delete', danger: true, onClick: () => onDelete() },
  ];

  return (
    <Card
      size="small"
      data-testid={`lead-card-${lead.id}`}
      data-overdue={overdue ? 'true' : 'false'}
      style={{
        marginBottom: 10,
        cursor: 'pointer',
        border: overdue
          ? `1px solid ${token.colorErrorBorder}`
          : `1px solid ${border}33`,
        borderLeft: `4px solid ${border}`,
        opacity: isUpdating ? 0.65 : 1,
        boxShadow: token.boxShadowTertiary,
      }}
      styles={{ body: { padding: 12 } }}
      onClick={onView}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        {!converted && dragHandleProps && (
          <div
            {...dragHandleProps}
            onClick={(e) => e.stopPropagation()}
            style={{
              cursor: 'grab',
              color: token.colorTextQuaternary,
              paddingTop: 4,
              touchAction: 'none',
            }}
            aria-label="Drag lead"
          >
            <HolderOutlined />
          </div>
        )}
        <Avatar
          size={36}
          style={{
            backgroundColor: border,
            flexShrink: 0,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {companyInitials(lead.customer_name)}
        </Avatar>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}>
            <Text strong ellipsis style={{ fontSize: 14, flex: 1 }}>
              {lead.customer_name}
            </Text>
            <Dropdown
              trigger={['click']}
              menu={{ items: menuItems, onClick: ({ domEvent }) => domEvent.stopPropagation() }}
            >
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                style={{ padding: '0 4px', color: token.colorTextSecondary }}
              >
                ⋮
              </span>
            </Dropdown>
          </div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {lead.lead_number}
          </Text>
          <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            <Tag color={PRIORITY_COLORS[lead.priority] || 'default'} style={{ margin: 0, fontSize: 11 }}>
              {lead.priority}
            </Tag>
            {lead.sales_team_name && (
              <Tag style={{ margin: 0, fontSize: 11 }}>{lead.sales_team_name}</Tag>
            )}
            {lead.lead_score > 0 && (
              <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>
                Score {lead.lead_score}
              </Tag>
            )}
            {(lead as { qualification_level?: number }).qualification_level && (
              <Tag style={{ margin: 0, fontSize: 11 }}>
                L{(lead as { qualification_level?: number }).qualification_level}
              </Tag>
            )}
            {converted && (
              <Tag color="green" style={{ margin: 0, fontSize: 11 }}>
                Converted
              </Tag>
            )}
          </div>
          <Space direction="vertical" size={2} style={{ marginTop: 8, width: '100%' }}>
            <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
              <UserOutlined /> {lead.contact_person || '—'}
              {ownerLabel !== '—' ? ` · ${ownerLabel}` : ''}
            </Text>
            {lead.phone && (
              <Text type="secondary" style={{ fontSize: 11 }} ellipsis>
                <PhoneOutlined /> {lead.phone}
              </Text>
            )}
            {lead.email && (
              <Text type="secondary" style={{ fontSize: 11 }} ellipsis>
                <MailOutlined /> {lead.email}
              </Text>
            )}
            {rev > 0 && (
              <Text strong style={{ color: token.colorSuccess, fontSize: 13 }}>
                {formatCurrency(rev)}
              </Text>
            )}
            {lead.next_follow_up_date && (
              <Tag
                color={overdue ? 'error' : 'processing'}
                style={{ margin: 0, fontSize: 10 }}
                data-testid="follow-up-tag"
              >
                Follow-up: {dayjs(lead.next_follow_up_date).format('DD MMM')}
              </Tag>
            )}
            {converted && lead.converted_to_opportunity_id && (
              <Link
                to={`/growth/opportunities/${lead.converted_to_opportunity_id}`}
                onClick={(e) => e.stopPropagation()}
                style={{ fontSize: 11 }}
              >
                View opportunity
              </Link>
            )}
          </Space>
        </div>
      </div>
    </Card>
  );
};
